import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { MessageCircle, Check, X, Loader2, AlertCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const WhatsAppTab = ({ leagueId, token, isOwner }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Check if phone is already registered on mount
  useEffect(() => {
    const checkRegistration = async () => {
      if (!isOwner || !leagueId || !token) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/leagues/${leagueId}/whatsapp/phone`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.is_registered) {
            setIsRegistered(true);
            setPhoneNumber(data.phone_number || '');
          }
        }
      } catch (err) {
        console.error('Error checking WhatsApp registration:', err);
      } finally {
        setIsLoading(false);
      }
    };

    checkRegistration();
  }, [leagueId, token, isOwner]);

  const handleRegisterPhone = async () => {
    if (!phoneNumber || phoneNumber.length < 10) {
      toast.error('Inserisci un numero di telefono valido');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/leagues/${leagueId}/whatsapp/phone`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ phone_number: phoneNumber }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Numero WhatsApp registrato con successo!');
        setIsRegistered(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nella registrazione del numero');
      }
    } catch (err) {
      toast.error('Errore di connessione');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemovePhone = async () => {
    setIsDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/leagues/${leagueId}/whatsapp/phone`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success('Numero WhatsApp rimosso con successo!');
        setIsRegistered(false);
        setPhoneNumber('');
        setShowDeleteDialog(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nella rimozione del numero');
      }
    } catch (err) {
      toast.error('Errore di connessione');
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOwner) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center space-y-4">
          <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Accesso Limitato
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Solo gli owner della lega possono gestire l'integrazione WhatsApp.
          </p>
        </div>
      </div>
    );
  }

  // Show loading state while checking registration
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 text-blue-500 mx-auto animate-spin" />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Caricamento...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Setup Card */}
      <Card className="border-2 border-green-200 dark:border-green-900 bg-gradient-to-br from-green-50 to-white dark:from-green-950 dark:to-gray-900">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-500 rounded-lg">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-xl">Controllo via WhatsApp</CardTitle>
              <CardDescription>
                Gestisci la tua lega direttamente da WhatsApp
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {!isRegistered ? (
            <>
              {/* Instructions */}
              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-3">
                <h4 className="font-semibold text-blue-900 dark:text-blue-100 flex items-center gap-2">
                  <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
                  Registra il tuo numero
                </h4>
                <p className="text-sm text-blue-800 dark:text-blue-200 ml-8">
                  Inserisci il numero WhatsApp che userai per controllare la lega.
                  Usa il formato internazionale (es: +393331234567).
                </p>
              </div>

              {/* Phone Input */}
              <div className="space-y-2">
                <Label htmlFor="phone">Numero WhatsApp</Label>
                <div className="flex gap-2">
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+393331234567"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="flex-1"
                  />
                  <Button 
                    onClick={handleRegisterPhone}
                    disabled={isSubmitting || !phoneNumber}
                    className="bg-green-500 hover:bg-green-600"
                  >
                    {isSubmitting ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Registrazione...</>
                    ) : (
                      <><Check className="w-4 h-4 mr-2" /> Registra</>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Formato: +39 per Italia, +41 per Svizzera, ecc.
                </p>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              {/* Success Message */}
              <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                    <div>
                      <h4 className="font-semibold text-green-900 dark:text-green-100">
                        Numero registrato con successo!
                      </h4>
                      <p className="text-sm text-green-800 dark:text-green-200 mt-1">
                        {phoneNumber}
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDeleteDialog(true)}
                    className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Rimuovi
                  </Button>
                </div>
              </div>

              {/* Next Steps */}
              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-3">
                <h4 className="font-semibold text-blue-900 dark:text-blue-100 flex items-center gap-2">
                  <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">2</span>
                  Connetti WhatsApp al Bot
                </h4>
                <p className="text-sm text-blue-800 dark:text-blue-200 ml-8">
                  Ora devi avviare il bot WhatsApp sul server e scansionare il QR code.
                  Contatta l'amministratore del sistema per completare l'attivazione.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Commands Guide Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">📱 Comandi Disponibili</CardTitle>
          <CardDescription>
            Una volta connesso, potrai usare questi comandi via WhatsApp
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Info Commands */}
            <div>
              <h4 className="font-semibold text-sm mb-2 text-gray-700 dark:text-gray-300">
                📋 Comandi Informativi
              </h4>
              <div className="space-y-2 ml-4">
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="font-mono text-xs">
                    /help
                  </Badge>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Mostra menu comandi
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="font-mono text-xs">
                    /classifica
                  </Badge>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Visualizza classifica lega
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="font-mono text-xs">
                    /stats
                  </Badge>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Statistiche dettagliate
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="font-mono text-xs">
                    /scambi
                  </Badge>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Storico completo scambi lega
                  </span>
                </div>
              </div>
            </div>

            {/* Admin Commands */}
            <div>
              <h4 className="font-semibold text-sm mb-2 text-gray-700 dark:text-gray-300">
                ⚙️ Comandi Amministrativi
              </h4>
              <div className="space-y-2 ml-4">
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="font-mono text-xs">
                    /crea_giornata
                  </Badge>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Crea nuova giornata
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="font-mono text-xs">
                    /calcola_giornata
                  </Badge>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Calcola giornata corrente
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="font-mono text-xs">
                    /annulla_giornata
                  </Badge>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Annulla ultima giornata
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="font-mono text-xs">
                    /bonus &lt;prof&gt; &lt;tipo&gt;
                  </Badge>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Aggiungi bonus/malus a professore
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="font-mono text-xs">
                    /bonus list
                  </Badge>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Lista professori e bonus disponibili
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Notice */}
      <div className="bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-yellow-900 dark:text-yellow-100 text-sm">
              Nota sulla Sicurezza
            </h4>
            <p className="text-sm text-yellow-800 dark:text-yellow-200 mt-1">
              Mantieni il tuo numero WhatsApp privato. Solo i numeri registrati possono
              eseguire comandi amministrativi sulla lega.
            </p>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rimuovere numero WhatsApp?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Sei sicuro di voler rimuovere il numero WhatsApp <strong>{phoneNumber}</strong> dalla lega?
              </p>
              <p className="text-red-600 dark:text-red-400 font-medium">
                ⚠️ Non potrai più usare i comandi WhatsApp per questa lega finché non registri nuovamente un numero.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemovePhone}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Rimozione...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Rimuovi Numero
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
